import React, { useEffect, useState } from "react";
import axios from "axios";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Check, CreditCard, Shield, ArrowRight, Sparkles, Gift, Clock, Zap, Crown } from "lucide-react";
import { PageHeader, Spinner } from "../components/Brand";
import { useApp, API } from "../AppContext";

const PLAN_META = {
  starter: { color: "#F5F5F5", featured: false },
  pro: { color: "#D4AF37", featured: true },
  enterprise: { color: "#C00000", featured: false },
  ecosystem: { color: "#F5D062", featured: false, custom: true },
};

export default function Billing() {
  const { lang, user } = useApp();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const requested = params.get("plan");
  const [plans, setPlans] = useState(null);
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(false);
  const [trialLoading, setTrialLoading] = useState(false);
  const [err, setErr] = useState("");
  const [trialOk, setTrialOk] = useState(false);

  const loadSub = () => axios.get(`${API}/billing/me`).then(({ data }) => setSub(data)).catch(() => {});

  useEffect(() => {
    axios.get(`${API}/billing/plans`).then(({ data }) => {
      // Normalize: backend returns {success, plans: [...]} — index by id
      const arr = Array.isArray(data?.plans) ? data.plans : Object.values(data?.plans || data || {});
      const byId = {};
      arr.forEach((p) => { if (p && p.id) byId[p.id] = p; });
      setPlans(byId);
    });
    loadSub();
  }, []);

  useEffect(() => {
    if (requested && plans && plans[requested] && !loading && !sub?.active) {
      startCheckout(requested);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requested, plans]);

  const startCheckout = async (planId) => {
    setErr(""); setLoading(true);
    try {
      const { data } = await axios.post(`${API}/billing/checkout`, {
        plan_id: planId,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      setErr(e?.response?.data?.detail || (lang === "fr" ? "Erreur lors de la création du paiement" : "Failed to create checkout"));
      setLoading(false);
    }
  };

  const startProTrial = async () => {
    setErr(""); setTrialLoading(true);
    try {
      await axios.post(`${API}/billing/start-pro-trial`);
      await loadSub();
      setTrialOk(true);
      setTimeout(() => setTrialOk(false), 4000);
    } catch (e) {
      setErr(e?.response?.data?.detail || (lang === "fr" ? "Impossible d'activer l'essai" : "Could not start trial"));
    } finally {
      setTrialLoading(false);
    }
  };

  const trialDaysLeft = (() => {
    if (!sub?.pro_trial_until) return 0;
    const ms = new Date(sub.pro_trial_until) - new Date();
    return Math.max(0, Math.ceil(ms / 86400000));
  })();

  const T = lang === "fr" ? {
    title: "Abonnement",
    sub: "Choisissez le forfait qui correspond à votre équipe. Paiement sécurisé par Stripe.",
    current: "Plan actuel",
    active: "Actif",
    inactive: "Aucun abonnement actif",
    redirect: "Redirection vers le paiement sécurisé...",
    starter_desc: "1 utilisateur · 25 générations IA/mois",
    pro_desc: "Jusqu'à 3 utilisateurs · 150 générations IA/mois",
    enterprise_desc: "Jusqu'à 10 utilisateurs · 500 générations IA/mois",
    ecosystem_desc: "Utilisateurs personnalisés · Tous les modules",
    popular: "Le plus populaire",
    cta: "Choisir ce forfait",
    cta_current: "Forfait courant",
    cta_contact: "Contacter les ventes",
    month: "/ mois",
    custom_price: "Sur devis",
    secure: "Paiement sécurisé par Stripe · Annulation à tout moment · Prix en CAD, taxes en sus.",
    starter_f: [
      "Création de devis professionnels",
      "Gestion clients, projets, bibliothèque de prix",
      "Taxes TPS/TVQ, frais généraux et profits",
      "Export PDF, envoi par courriel",
      "Modèles de devis personnalisables",
      "IA · 25 générations/mois",
      "Support standard",
    ],
    pro_f: [
      "Tout le forfait Démarrage",
      "IA avancée · descriptions & postes auto",
      "Assemblages personnalisés · bibliothèque de prix",
      "Import/Export Excel · duplication de devis",
      "Versions, révisions, comparaison",
      "Signature électronique · conversion en projet/facture",
      "Connexion PROGÈRE CRM & COMPTA",
      "IA · 150 générations/mois · Support prioritaire",
    ],
    enterprise_f: [
      "Tout le forfait Professionnel",
      "Multi-départements · bibliothèque corporative",
      "Approbations internes · permissions avancées",
      "Taux par employé, métier, catégorie",
      "Analyse avancée des coûts · marges par projet",
      "Tableaux de bord de performance · automatisations",
      "Intégration CORE, CRM, ERP, COMPTA, PUNCH, TAKEOFF",
      "IA · 500 générations/mois · Support entreprise",
    ],
    ecosystem_f: [
      "PROGÈRE CORE · PUNCH · CRM · ERP",
      "PROGÈRE COMPTA · PAIE · DEVIS AI · TAKEOFF",
      "Gestion multi-entreprises",
      "Accompagnement d'implantation · Formation",
      "Migration de données · personnalisation",
      "Intégrations sur mesure · Support VIP",
      "Contrat annuel disponible",
    ],
    free_label: "Version d'essai gratuite",
    free_remaining: "soumissions restantes",
    free_used: "Vous avez utilisé toutes vos soumissions gratuites",
    trial_pro_title: "Essai gratuit Pro — 14 jours",
    trial_pro_sub: "Toutes les fonctionnalités Pro débloquées. Aucune carte requise. Une seule activation par compte.",
    trial_pro_cta: "Démarrer mon essai gratuit",
    trial_pro_ongoing: "Essai Pro en cours",
    trial_pro_days_left: (n) => `${n} jour${n > 1 ? "s" : ""} restant${n > 1 ? "s" : ""}`,
    trial_pro_used: "Essai déjà utilisé",
    trial_ok: "✓ Essai Pro activé — vous avez maintenant accès à toutes les fonctionnalités Pro pendant 14 jours.",
  } : {
    title: "Subscription",
    sub: "Choose the plan that fits your team. Secure Stripe billing.",
    current: "Current plan",
    active: "Active",
    inactive: "No active subscription",
    redirect: "Redirecting to secure checkout...",
    starter_desc: "1 user · 25 AI generations/mo",
    pro_desc: "Up to 3 users · 150 AI generations/mo",
    enterprise_desc: "Up to 10 users · 500 AI generations/mo",
    ecosystem_desc: "Custom users · All modules",
    popular: "Most popular",
    cta: "Choose this plan",
    cta_current: "Current plan",
    cta_contact: "Contact sales",
    month: "/ month",
    custom_price: "On request",
    secure: "Secure Stripe payment · Cancel anytime · Prices in CAD, taxes extra.",
    starter_f: [
      "Professional quote creation",
      "Clients, projects, price library",
      "GST/QST, overhead & profit",
      "PDF export, email sending",
      "Custom quote templates",
      "AI · 25 generations/month",
      "Standard support",
    ],
    pro_f: [
      "All Starter features",
      "Advanced AI · auto descriptions & items",
      "Custom assemblies · custom price library",
      "Excel import/export · quote duplication",
      "Versions, revisions, comparison",
      "E-signature · convert to project/invoice",
      "PROGÈRE CRM & COMPTA integration",
      "AI · 150 generations/month · Priority support",
    ],
    enterprise_f: [
      "All Professional features",
      "Multi-department · corporate library",
      "Internal approvals · advanced permissions",
      "Rates by employee, trade, category",
      "Advanced cost analysis · per-project margins",
      "Performance dashboards · automations",
      "CORE, CRM, ERP, COMPTA, PUNCH, TAKEOFF integration",
      "AI · 500 generations/month · Enterprise support",
    ],
    ecosystem_f: [
      "PROGÈRE CORE · PUNCH · CRM · ERP",
      "PROGÈRE COMPTA · PAYROLL · DEVIS AI · TAKEOFF",
      "Multi-company management",
      "Onboarding · Training",
      "Data migration · customization",
      "Custom integrations · VIP support",
      "Annual contract available",
    ],
    free_label: "Free trial",
    free_remaining: "quotes left",
    free_used: "You have used all your free quotes",
    trial_pro_title: "Free Pro trial — 14 days",
    trial_pro_sub: "All Pro features unlocked. No credit card required. One activation per account.",
    trial_pro_cta: "Start my free trial",
    trial_pro_ongoing: "Pro trial in progress",
    trial_pro_days_left: (n) => `${n} day${n > 1 ? "s" : ""} left`,
    trial_pro_used: "Trial already used",
    trial_ok: "✓ Pro trial activated — you now have access to all Pro features for 14 days.",
  };

  if (!plans) return <Spinner />;

  const PLAN_CARDS = [
    { id: "starter", name: "Starter", price: plans.starter?.monthly_amount ?? plans.starter?.amount ?? 99, desc: T.starter_desc, features: T.starter_f, ...PLAN_META.starter },
    { id: "pro", name: "Pro", price: plans.pro?.monthly_amount ?? plans.pro?.amount ?? 249, desc: T.pro_desc, features: T.pro_f, ...PLAN_META.pro },
    { id: "enterprise", name: "Entreprise", price: plans.enterprise?.monthly_amount ?? plans.enterprise?.amount ?? 499, desc: T.enterprise_desc, features: T.enterprise_f, ...PLAN_META.enterprise },
    { id: "ecosystem", name: lang === "fr" ? "Écosystème complet" : "Full Ecosystem", price: null, desc: T.ecosystem_desc, features: T.ecosystem_f, ...PLAN_META.ecosystem },
  ];

  return (
    <div data-testid="billing-page">
      <PageHeader title={T.title} subtitle={T.sub} />

      {/* Active plan card — PROGÈRE PUNCH style */}
      {(sub?.active || sub?.in_pro_trial) && (
        <div className="rounded-2xl p-6 mb-6 max-w-5xl"
          style={{
            background: "linear-gradient(180deg, rgba(24,24,24,0.9) 0%, rgba(11,11,11,0.95) 100%)",
            border: "1px solid rgba(212,175,55,0.35)",
            boxShadow: "0 12px 40px -12px rgba(212,175,55,0.25), inset 0 1px 0 rgba(212,175,55,0.08)",
          }}
          data-testid="active-plan-card">
          <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] font-semibold mb-1" style={{ color: "#D4AF37" }}>
                {lang === "fr" ? "Forfait actif" : "Active plan"}
              </div>
              <div className="font-heading text-2xl font-black tracking-tight" style={{ color: "#F5F5F5" }}>
                PROGÈRE DEVIS AI {sub?.plan === "pro" ? "Pro" : sub?.plan === "enterprise" ? "Entreprise" : "Starter"}
              </div>
              <div className="flex items-center gap-2 mt-1">
                <span className="w-2 h-2 rounded-full" style={{ background: "#4ADE80", boxShadow: "0 0 8px #4ADE80" }} />
                <span className="text-xs font-semibold" style={{ color: "#4ADE80" }}>
                  {sub?.in_pro_trial ? T.trial_pro_ongoing : T.active}
                </span>
              </div>
            </div>
            <div className="text-right">
              <div className="font-heading text-3xl font-black" style={{ color: "#F5F5F5" }}>
                {plans[sub?.plan || "starter"]?.amount || 99}
                <span className="text-sm font-semibold ml-1" style={{ color: "rgba(245,245,245,0.6)" }}>CAD</span>
              </div>
              <div className="text-xs" style={{ color: "rgba(245,245,245,0.5)" }}>{T.month}</div>
            </div>
          </div>

          {/* Limits progress bars */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {(() => {
              const LIMITS = {
                starter: { users: 2, quotes: 25, ai: 25 },
                pro: { users: 8, quotes: 150, ai: 250 },
                enterprise: { users: "∞", quotes: "∞", ai: "∞" },
              };
              const l = LIMITS[sub?.plan] || LIMITS.starter;
              const used = { users: 1, quotes: sub?.quotes_used || 0, ai: sub?.ai_used || 0 };
              const items = [
                { label: lang === "fr" ? "Utilisateurs" : "Users", used: used.users, limit: l.users, color: "#D4AF37" },
                { label: lang === "fr" ? "Soumissions ce mois" : "Quotes this month", used: used.quotes, limit: l.quotes, color: "#4ADE80" },
                { label: lang === "fr" ? "Générations IA" : "AI generations", used: used.ai, limit: l.ai, color: "#C00000" },
              ];
              return items.map((it) => {
                const pct = it.limit === "∞" ? 12 : Math.min(100, (it.used / it.limit) * 100);
                return (
                  <div key={it.label} className="rounded-xl p-3" style={{ background: "rgba(11,11,11,0.5)", border: "1px solid rgba(245,245,245,0.06)" }}>
                    <div className="flex items-baseline justify-between mb-2">
                      <div className="text-[10px] uppercase tracking-widest" style={{ color: "rgba(245,245,245,0.5)" }}>{it.label}</div>
                      <div className="text-xs font-bold" style={{ color: "#F5F5F5" }}>
                        {it.used}<span style={{ color: "rgba(245,245,245,0.5)" }}> / {it.limit}</span>
                      </div>
                    </div>
                    <div className="w-full h-1.5 rounded-full overflow-hidden" style={{ background: "rgba(245,245,245,0.08)" }}>
                      <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: it.color, boxShadow: `0 0 8px ${it.color}80` }} />
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        </div>
      )}

      {/* Old fallback card for inactive users */}
      {!(sub?.active || sub?.in_pro_trial) && (
      <div className="glass-card p-5 mb-6 flex items-center justify-between max-w-5xl" data-testid="billing-current">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: "rgba(245,245,245,0.06)" }}>
            <Shield size={18} color="rgba(245,245,245,0.5)" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest" style={{ color: "rgba(245,245,245,0.5)" }}>{T.current}</div>
            <div className="font-heading text-lg font-bold" style={{ color: "#F5F5F5" }}>
              <span style={{ color: "rgba(245,245,245,0.5)" }}>{T.inactive}</span>
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Free quota indicator (only when no active sub / trial) */}
      {sub && !sub.active && !sub.in_pro_trial && (
        <div className="glass-card p-5 mb-6 max-w-5xl" data-testid="free-quota-card" style={{ borderLeft: "3px solid #D4AF37" }}>
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: "rgba(212,175,55,0.15)" }}>
                <Zap size={18} color="#D4AF37" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest" style={{ color: "#D4AF37" }}>{T.free_label}</div>
                <div className="font-heading text-base font-bold" style={{ color: "#F5F5F5" }}>
                  {sub.trial_quotes_remaining > 0 ? (
                    <>
                      <span style={{ color: "#D4AF37" }}>{sub.trial_quotes_remaining}</span> {T.free_remaining}
                    </>
                  ) : (
                    <span style={{ color: "rgba(245,245,245,0.6)" }}>{T.free_used}</span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex gap-1.5">
              {[0, 1, 2].map((i) => (
                <div key={i} className="w-8 h-2 rounded-full transition-all"
                  style={{ background: i < (sub.trial_quotes_remaining || 0) ? "#D4AF37" : "rgba(245,245,245,0.1)" }} />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Pro Trial CTA */}
      {sub && !sub.active && !sub.in_pro_trial && !sub.pro_trial_used && (
        <div className="p-6 mb-8 max-w-5xl relative overflow-hidden" data-testid="pro-trial-cta"
          style={{
            background: "linear-gradient(135deg, rgba(212,175,55,0.12) 0%, rgba(192,0,0,0.08) 100%)",
            border: "1px solid rgba(212,175,55,0.35)",
            borderRadius: "24px",
          }}>
          <div className="absolute top-0 right-0 w-40 h-40 rounded-full opacity-30" style={{ background: "radial-gradient(circle, #D4AF37 0%, transparent 70%)", transform: "translate(40%, -40%)" }} />
          <div className="relative flex items-center justify-between gap-6 flex-wrap">
            <div className="flex items-start gap-4 flex-1 min-w-[280px]">
              <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: "rgba(212,175,55,0.2)" }}>
                <Gift size={22} color="#D4AF37" />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-heading text-xl font-bold" style={{ color: "#F5F5F5" }}>{T.trial_pro_title}</h3>
                  <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full" style={{ background: "#C00000", color: "#F5F5F5" }}>14j</span>
                </div>
                <p className="text-sm" style={{ color: "rgba(245,245,245,0.7)" }}>{T.trial_pro_sub}</p>
              </div>
            </div>
            <button onClick={startProTrial} disabled={trialLoading}
              className="btn-gold whitespace-nowrap disabled:opacity-50"
              data-testid="start-pro-trial-btn">
              {trialLoading ? <Spinner /> : <><Sparkles size={14} /> {T.trial_pro_cta}</>}
            </button>
          </div>
          {trialOk && (
            <div className="mt-4 px-4 py-2 rounded-xl text-sm" style={{ background: "rgba(74,222,128,0.12)", color: "#4ADE80", border: "1px solid rgba(74,222,128,0.3)" }} data-testid="trial-ok-msg">
              {T.trial_ok}
            </div>
          )}
        </div>
      )}

      {/* Loading state when redirecting */}
      {loading && (
        <div className="glass-card p-8 mb-6 max-w-5xl flex items-center gap-4" data-testid="billing-redirect">
          <Spinner />
          <div style={{ color: "#F5F5F5" }}>{T.redirect}</div>
        </div>
      )}

      {err && (
        <div className="px-4 py-3 rounded-xl mb-6 max-w-5xl" style={{ background: "rgba(192,0,0,0.15)", color: "#FF6B6B", border: "1px solid rgba(192,0,0,0.35)" }} data-testid="billing-error">
          {err}
        </div>
      )}

      {/* Plans grid */}
      {/* Plans grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 max-w-6xl">
        {PLAN_CARDS.map((p) => {
          const isCurrent = sub?.active && sub.plan === p.id;
          const isCustom = p.custom || p.price == null;
          return (
            <div key={p.id} className={`p-8 relative ${p.featured ? "glass-strong glow-gold" : "glass-card"}`}
              style={p.featured ? { border: "1px solid rgba(212,175,55,0.5)" } : {}}
              data-testid={`billing-plan-${p.id}`}>
              {p.featured && (
                <div className="absolute -top-3 left-8 px-3 py-1 rounded-full text-[10px] uppercase tracking-widest font-bold"
                  style={{ background: "#C00000", color: "#F5F5F5" }}>
                  <Sparkles size={10} className="inline mr-1" />{T.popular}
                </div>
              )}
              <h3 className="font-heading text-2xl font-bold mb-1" style={{ color: p.color }}>{p.name}</h3>
              <p className="text-sm mb-6" style={{ color: "rgba(245,245,245,0.5)" }}>{p.desc}</p>
              <div className="mb-6 flex items-baseline gap-1 flex-wrap">
                {isCustom ? (
                  <span className="font-heading text-3xl font-black" style={{ color: "#F5F5F5" }}>{T.custom_price}</span>
                ) : (
                  <>
                    <span className="font-heading text-5xl font-black" style={{ color: "#F5F5F5" }}>
                      {lang === "fr" ? `${p.price.toFixed(0)} $` : `$${p.price.toFixed(0)}`}
                    </span>
                    <span style={{ color: "rgba(245,245,245,0.5)" }}>{T.month}</span>
                  </>
                )}
              </div>
              <ul className="space-y-3 mb-8 text-sm">
                {p.features.map((f) => (
                  <li key={f} className="flex gap-2 items-start" style={{ color: "rgba(245,245,245,0.78)" }}>
                    <Check size={16} color="#D4AF37" className="mt-0.5 flex-shrink-0" /> {f}
                  </li>
                ))}
              </ul>
              {isCustom ? (
                <a href="mailto:ventes@progere.ca?subject=PROGÈRE%20Écosystème%20complet"
                  className="btn-gold w-full justify-center"
                  data-testid={`billing-subscribe-${p.id}`}>
                  {T.cta_contact} <ArrowRight size={14} />
                </a>
              ) : (
                <button
                  disabled={loading || isCurrent}
                  onClick={() => startCheckout(p.id)}
                  className={`${p.featured ? "btn-red" : "btn-ghost"} w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed`}
                  data-testid={`billing-subscribe-${p.id}`}>
                  {isCurrent ? T.cta_current : <>{T.cta} <ArrowRight size={14} /></>}
                </button>
              )}
            </div>
          );
        })}
      </div>

      <p className="text-xs mt-6 max-w-5xl text-center" style={{ color: "rgba(245,245,245,0.4)" }}>
        🔒 {T.secure}
      </p>
    </div>
  );
}
